// src/pages/CostOfProduction.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

export default function CostOfProduction() {
  const { projectId } = useParams();

  // ─── State Hooks ───────────────────────────────────────────
  const [projectYears, setProjectYears] = useState([]);
  const [capUtil, setCapUtil] = useState([]);
  const [rmRows, setRmRows] = useState([]); // RM consumption inputs
  const [rmPartRows, setRmPartRows] = useState([]); // RM particulars
  const [products, setProducts] = useState([]); // product names
  const [costRows, setCostRows] = useState([]); // dynamic % rows
  const [labourData, setLabourData] = useState([]);
  const [labourExtra, setLabourExtra] = useState(0);
  const [powerData, setPowerData] = useState([]);
  const [manualConsumables, setManualConsumables] = useState([]);
  const [consumablesPct, setConsumablesPct] = useState(0);
  const [termLoanInput, setTermLoanInput] = useState({ startMonth: "" });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ─── Fetch from API ────────────────────────────────────────
  useEffect(() => {
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        console.log("🔥 project-inputs payload:", data);
        setProjectYears(data.projectYears || []);
        setCapUtil(data.capacityUtilizationPerYear || []);
        setRmRows(data.rmConsumptionInputs || []);
        setRmPartRows(data.rmConsumptionParticulars || []);
        setProducts((data.manufacturedProducts || []).map((p) => p.product));
        setCostRows(data.costOfProductionData || []);
        setLabourData(data.labourCost || []);
        setLabourExtra(parseFloat(data.labourExtraPercent) || 0);
        setPowerData(data.powerSectionData || []);
        setManualConsumables(data.manualConsumables || []);
        setConsumablesPct(parseFloat(data.consumablesExpensePct) || 0);
        setTermLoanInput(data.termLoanInput || { startMonth: "" });
      })
      .catch((e) => {
        console.error(e);
        setError("Failed to load data.");
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  // ─── Parse "mm-yyyy" ───────────────────────────────────────
  const prodStart = useMemo(() => {
    const [m, y] = (termLoanInput.startMonth || "")
      .split("-")
      .map((t) => parseInt(t, 10));
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  // ─── Months active in FY ───────────────────────────────────
  const getMonthsActive = useCallback(
    (fyLabel) => {
      if (!prodStart) return 12;
      const fyStart = parseInt(fyLabel.split("-")[0], 10);
      const raw =
        (fyStart + 1 - prodStart.year) * 12 + (3 - prodStart.month + 1);
      return Math.max(0, Math.min(raw, 12));
    },
    [prodStart]
  );

  // ─── 1) Compute installed capacity per product ─────────────
  const withInstalled = useMemo(
    () =>
      rmRows.map((r) => {
        const d = Number(r.workingDays) || 0;
        const h = Number(r.workingHoursPerDay) || 0;
        const o = Number(r.outputPerHour) || 0;
        return { ...r, installed: (d * h * o) / 1000 };
      }),
    [rmRows]
  );

  // ─── 2) Production per product per FY ──────────────────────
  const productionByProductYear = useMemo(() => {
    const out = {};
    products.forEach((prod) => {
      const inst =
        withInstalled.find((r) => r.products === prod)?.installed || 0;
      out[prod] = projectYears.map((fy, yi) => {
        const u = Number(capUtil[yi]) || 0;
        const m = getMonthsActive(fy);
        return (inst * u * m) / (100 * 12);
      });
    });
    return out;
  }, [products, projectYears, capUtil, withInstalled, getMonthsActive]);

  // ─── 3) Total production per FY ────────────────────────────
  const totalProductionByYear = useMemo(
    () =>
      projectYears.map((_, yi) =>
        products.reduce(
          (s, p) => s + (productionByProductYear[p]?.[yi] || 0),
          0
        )
      ),
    [projectYears, products, productionByProductYear]
  );

  // ─── 4) Raw‐material cost (lacs) per product per FY ─────────
  const costLacsByProductYear = useMemo(() => {
    const map = {};
    products.forEach((prod) => {
      map[prod] = productionByProductYear[prod].map((mt) => {
        return +rmPartRows
          .filter((p) => p.product === prod)
          .reduce((sum, p) => {
            const qty = Number(p.quantity) || 0; // KG per MT
            const price = Number(p.price) || 0; // Rs per KG
            const cons = mt * qty; // KG consumed
            const costMT = price * 1000; // Rs per MT
            return sum + (cons * costMT) / 100000; // lacs
          }, 0)
          .toFixed(2);
      });
    });
    return map;
  }, [products, productionByProductYear, rmPartRows]);

  // ─── helper to sum A ────────────────────────────────────────
  const totalA = useCallback(
    (yi) =>
      products.reduce(
        (sum, p) => sum + Number(costLacsByProductYear[p]?.[yi] || 0),
        0
      ),
    [products, costLacsByProductYear]
  );

  // ─── 5) Labour cost per FY (lacs) ───────────────────────────
  const installedArr = useMemo(
    () => withInstalled.map((r) => r.installed),
    [withInstalled]
  );
  const totalAnnualProduction = useMemo(
    () => installedArr.reduce((s, v) => s + v, 0),
    [installedArr]
  );
  const yearWiseOutput = useMemo(
    () =>
      projectYears.map((fy, i) => {
        const u = Number(capUtil[i]) || 0;
        const m = getMonthsActive(fy);
        return installedArr
          .map((inst) => (inst * u * m) / (100 * 12))
          .reduce((s, v) => s + v, 0);
      }),
    [projectYears, capUtil, installedArr, getMonthsActive]
  );

  const totalAmount = useMemo(
    () =>
      labourData.reduce(
        (s, r) => s + (Number(r.nos) || 0) * (Number(r.ratePerMonth) || 0) * 12,
        0
      ),
    [labourData]
  );
  const totalExtras = (totalAmount * labourExtra) / 100;
  const totalLabourCost = totalAmount + totalExtras;
  const costPerMTLabour =
    totalAnnualProduction > 0 ? totalLabourCost / totalAnnualProduction : 0;
  const labourCostPerYear = useMemo(
    () =>
      yearWiseOutput.map((mt) => +((mt * costPerMTLabour) / 100000).toFixed(2)),
    [yearWiseOutput, costPerMTLabour]
  );

  // ─── 6) Power cost per FY (lacs) ───────────────────────────
  const powerCostPerYear = useMemo(() => {
    const findVal = (k) =>
      Number(powerData.find((r) => r.particular === k)?.value) || 0;
    const load = findVal("Total Connected Power Load (KW)");
    const pf = findVal("Power Factor");
    const rate = findVal("Average Rate per Unit");
    const fixM = findVal("Fixed Cost (Per Month)");
    const avgH =
      rmRows.reduce((s, r) => s + (Number(r.workingHoursPerDay) || 0), 0) /
      (rmRows.length || 1);
    const avgD =
      rmRows.reduce((s, r) => s + (Number(r.workingDays) || 0), 0) /
      (rmRows.length || 1);
    const dailyVar = load * pf * avgH * rate;
    const annualVar = dailyVar * avgD;
    const annualFix = fixM * 12;
    const costPerMTPower =
      totalAnnualProduction > 0
        ? (annualVar + annualFix) / totalAnnualProduction
        : 0;
    return totalProductionByYear.map(
      (mt) => +((mt * costPerMTPower) / 100000).toFixed(2)
    );
  }, [powerData, rmRows, totalAnnualProduction, totalProductionByYear]);

  // ─── 7) Consumables cost per FY (lacs) ─────────────────────
  const consumablesCostPerYear = useMemo(() => {
    const salesLacs = 354.91;
    const prodLacs = +(salesLacs * (consumablesPct / 100)).toFixed(2);
    const manLacs = manualConsumables.reduce(
      (s, r) => s + salesLacs * ((Number(r.taxPercent) || 0) / 100),
      0
    );
    const grandLacs = prodLacs + manLacs;
    const costPerMTLacs =
      totalAnnualProduction > 0 ? grandLacs / totalAnnualProduction : 0;
    return yearWiseOutput.map((mt) => +(mt * costPerMTLacs).toFixed(2));
  }, [
    manualConsumables,
    consumablesPct,
    totalAnnualProduction,
    yearWiseOutput,
  ]);

  // ─── 8) Detail B % rows per FY ─────────────────────────────
  const detailBPercent = useMemo(
    () =>
      projectYears.map((_, yi) =>
        costRows
          .reduce(
            (sum, r) => sum + ((Number(r.percentage) || 0) / 100) * totalA(yi),
            0
          )
          .toFixed(2)
      ),
    [projectYears, costRows, totalA]
  );

  // ─── Totals ────────────────────────────────────────────────
  const totalB = useMemo(
    () =>
      projectYears.map((_, yi) => {
        const L = labourCostPerYear[yi] || 0;
        const P = powerCostPerYear[yi] || 0;
        const C = consumablesCostPerYear[yi] || 0;
        const D = Number(detailBPercent[yi]) || 0;
        return +(L + P + C + D).toFixed(2);
      }),
    [
      projectYears,
      labourCostPerYear,
      powerCostPerYear,
      consumablesCostPerYear,
      detailBPercent,
    ]
  );
  const grandAB = useMemo(
    () => projectYears.map((_, yi) => +(totalA(yi) + totalB[yi]).toFixed(2)),
    [projectYears, totalA, totalB]
  );
  const avgCostPerMT = useMemo(
    () =>
      projectYears.map((_, yi) => {
        const tp = totalProductionByYear[yi] || 0;
        return tp > 0 ? +((grandAB[yi] / tp) * 100000).toFixed(2) : 0;
      }),
    [projectYears, grandAB, totalProductionByYear]
  );

  // ─── format helper ─────────────────────────────────────────
  const fmt = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : "";

  // ─── SAVE TO localStorage ──────────────────────────────────
  useEffect(() => {
    const totalCostArr = projectYears.map((_, yi) => totalA(yi));
    API.put(`/user/project-inputs/${projectId}`, {
      totalCostOfRawMaterial: totalCostArr,
    }).catch((err) => {
      console.error("Failed to update totalCostOfRawMaterial on server:", err);
    });
    API.put(`/user/project-inputs/${projectId}`, {
      averageCostOfProdPerMT: avgCostPerMT,
    }).catch((err) => {
      console.error("Failed to update averageCostOfProdPerMT on server:", err);
    });
    API.put(`/user/project-inputs/${projectId}`, {
      grandTotalCostOfProduction: grandAB,
    }).catch((err) => {
      console.error(
        "Failed to update grandTotalCostOfProduction on server:",
        err
      );
    });
  }, [projectYears, totalA, avgCostPerMT, grandAB]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-300 border-t-indigo-500" />
      </div>
    );
  }
  if (error) {
    return <div className="text-red-600 text-center py-8">{error}</div>;
  }

  return (
    <div className="bg-white space-y-12">
      {/* ─── Table A: Raw‐Material A ─────────────────────────────── */}
      <div>
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          Total Cost of Raw Material (A)
        </h2>
        <div className="overflow-x-auto mb-8 shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Product
                </th>
                {projectYears.map((yr, yi) => (
                  <th
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-center font-medium text-sm"
                  >
                    {yr}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map((p, pi) => (
                <tr key={pi} className="even:bg-gray-50">
                  <td className="px-2 py-1 border border-gray-200 text-sm text-gray-900">
                    {p}
                  </td>
                  {projectYears.map((_, yi) => (
                    <td
                      key={yi}
                      className="px-2 py-1 border border-gray-200 text-sm text-gray-700 text-center"
                    >
                      {fmt(costLacsByProductYear[p][yi])}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-gray-200 font-bold">
                <td className="px-2 py-1 border border-gray-200 text-sm text-gray-900">
                  Total (A)
                </td>
                {projectYears.map((_, yi) => (
                  <td
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-sm text-gray-700 text-center"
                  >
                    {fmt(totalA(yi))}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── Table B: Components & Totals ───────────────────────── */}
      <div>
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          Production Cost Components (B) & Totals
        </h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Component / Detail
                </th>
                {projectYears.map((yr, yi) => (
                  <th
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-center font-medium text-sm"
                  >
                    {yr}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Labour */}
              <tr>
                <td className="px-2 py-1 border border-gray-200 text-sm font-medium text-gray-900">
                  Labour & Other Charges
                </td>
                {labourCostPerYear.map((v, yi) => (
                  <td
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-sm text-gray-700 text-center"
                  >
                    {fmt(v)}
                  </td>
                ))}
              </tr>
              {/* Power */}
              <tr>
                <td className="px-2 py-1 border border-gray-200 text-sm font-medium text-gray-900">
                  Power
                </td>
                {powerCostPerYear.map((v, yi) => (
                  <td
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-sm text-gray-700 text-center"
                  >
                    {fmt(v)}
                  </td>
                ))}
              </tr>
              {/* Consumables */}
              <tr>
                <td className="px-2 py-1 border border-gray-200 text-sm font-medium text-gray-900">
                  Consumables
                </td>
                {consumablesCostPerYear.map((v, yi) => (
                  <td
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-sm text-gray-700 text-center"
                  >
                    {fmt(v)}
                  </td>
                ))}
              </tr>
              {/* Dynamic % */}
              {costRows.length ? (
                costRows.map((r, ri) => (
                  <tr key={ri} className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm text-gray-900">
                      {r.particular} ({r.percentage}% of A)
                    </td>
                    {projectYears.map((_, yi) => (
                      <td
                        key={yi}
                        className="px-2 py-1 border border-gray-200 text-sm text-gray-700 text-center"
                      >
                        {((r.percentage / 100) * totalA(yi)).toFixed(2)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr className="bg-gray-50">
                  <td
                    colSpan={1 + projectYears.length}
                    className="px-2 py-1 border border-gray-200 text-sm text-gray-500 italic text-center"
                  >
                    No dynamic cost rows configured.
                  </td>
                </tr>
              )}
              {/* Total B */}
              <tr className="bg-gray-100 font-bold">
                <td className="px-2 py-1 border border-gray-200 text-sm text-gray-900">
                  Total B
                </td>
                {totalB.map((v, yi) => (
                  <td
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-sm text-gray-700 text-center"
                  >
                    {fmt(v)}
                  </td>
                ))}
              </tr>
              {/* Grand A+B */}
              <tr className="bg-gray-200 font-bold">
                <td className="px-2 py-1 border border-gray-200 text-sm text-gray-900">
                  Grand Total (A+B)
                </td>
                {grandAB.map((v, yi) => (
                  <td
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-sm text-gray-700 text-center"
                  >
                    {fmt(v)}
                  </td>
                ))}
              </tr>
              {/* Average cost per MT */}
              <tr className="bg-gray-300 font-bold">
                <td className="px-2 py-1 border border-gray-200 text-sm text-gray-900">
                  Average Cost of Production per MT
                </td>
                {avgCostPerMT.map((v, yi) => (
                  <td
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-sm text-gray-700 text-center"
                  >
                    {fmt(v)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
